import theme from "../../../Styles/theme.scss";

// 08/07/24 - the colours key:value pair should be removed from the sensor data but at this point in time cannot. v1/v2 kit sensors are
// identified by colours, and this is shown on the front end. The sensors API response determines (via labelType key:value pair)
// whether a SensorCard should show a colour or a alphanumeric label (eg M1/P1). When Lilli recalls certain v2 sensors and the Cloud team
// modifies back end to remove references to v1/v2 sensor labelTypes etc, we can remove the colour/v1-v2 sensors references here.

// This function adds the additional data the frontend requires about the sensors paired with a hub
export const augmentSensorData = (devices) => {
  // Note: All name regex checks are taken from the backend implementation
  const additionalData = [
    {
      nameRegex: new RegExp("SquidZigBee$", "i"),
      displayName: "Lilli hub",
      img: "/images/installation/hub/hub.png",
      splinePNG: "/images/installation/spline/hub.png",
    },
    {
      defaultName: "Smart Plug Mini UK",
      nameRegex: new RegExp("Kitchen/[\\w]+/Power$", "i"), // Kitchen/<anything>/Power
      displayName: "Smart plug",
      img: "/images/installation/sensors/plug/plug_thumb.png",
      splinePNG: "/images/installation/spline/plug.png",
      alt: "plug icon",
      colour: theme.white,
      locationRequired: true,
      locationOptions: [
        { value: "kettle", label: "Kettle" },
        { value: "microwave", label: "Microwave" },
        { value: "toaster", label: "Toaster" },
        { value: "coffee maker", label: "Coffee maker" },
        { value: "other", label: "Other" },
      ],
    },
    {
      defaultName: "Motion Sensor Mini",
      nameRegex: new RegExp("Bathroom/Wall/Motion$", "i"), // Bathroom/Wall/Motion
      displayName: "Bathroom sensor",
      description:
        "This motion sensor is to be placed at any height in the bathroom. Be sure the sensor is as close to the bathing area as possible and not obstructed so it has as much view of the room as possible.",
      img: "/images/installation/sensors/motion/motion_thumb.png",
      splinePNG: "/images/installation/spline/motion.png",
      alt: "motion icon",
      colour: theme.secondary3,
      locationRequired: true,
      locationOptions: [
        { value: "Bath/Shower and Toilet", label: "Bath/Shower and Toilet" },
        { value: "Bath/Shower only", label: "Bath/Shower only" },
        { value: "toilet only", label: "Toilet only" },
      ],
    },
    {
      defaultName: "Motion Sensor Mini",
      nameRegex: new RegExp("Bedroom/Wall/Motion$", "i"), //Bedroom/Wall/Motion,
      displayName: "Bedroom sensor",
      description:
        "The sensor should be placed facing the entrance/exit to the bedroom and not facing the bed. It can be placed outside the bedroom.",
      img: "/images/installation/sensors/motion/motion_thumb.png",
      splinePNG: "/images/installation/spline/motion.png",
      alt: "motion icon",
      colour: theme.risk6,
      locationRequired: true,
      locationOptions: [
        {
          value: "Inside bedroom, facing the entrance",
          label: "Inside bedroom, facing the entrance",
        },
        {
          value: "Outside bedroom, facing entrance",
          label: "Outside bedroom, facing entrance",
        },
        {
          value: "Facing the bed (not recommended)",
          label: "Facing the bed (not recommended)",
        },
        {
          value: "bed skirting",
          label: "Bed skirting",
        },
        { value: "Other", label: "Other" },
      ],
    },
    {
      defaultName: "Motion Sensor Mini",
      nameRegex: new RegExp("Hall/Wall/Motion$", "i"), //Hall/Wall/Motion,
      displayName: "Motion sensor",
      description:
        "This motion sensor should be used to monitor Movement around the home. The sensor works best facing an area in the home that has high traffic throughout the day and should be placed above waist height.",
      img: "/images/installation/sensors/motion/motion_thumb.png",
      splinePNG: "/images/installation/spline/motion.png",
      alt: "motion icon",
      colour: theme.risk6,
      locationRequired: true,
      locationOptions: [
        { value: "hallway", label: "Hallway" },
        { value: "stairs", label: "Stairs" },
        { value: "living room", label: "Living Room" },
        { value: "kitchen", label: "Kitchen" },
        { value: "other", label: "Other" },
      ],
    },
    {
      defaultName: "Window Sensor",
      nameRegex: new RegExp("Kitchen/Fridge/Door$", "i"), // Kitchen/Fridge/Door
      displayName: "Fridge sensor",
      img: "/images/installation/sensors/door/door_thumb.png",
      splinePNG: "/images/installation/spline/door.png",
      alt: "door icon",
      colour: theme.primary6,
      locationRequired: true,
      locationOptions: [
        { value: "fridge", label: "Fridge" },
        { value: "cupboard", label: "Cupboard" },
        { value: "other", label: "Other" },
      ],
    },
    {
      defaultName: "Window Sensor",
      nameRegex: new RegExp(
        "[\\w]+/Front/Door$|[\\w]+/Back/Door$|[\\w]+/Main/Door$",
        "i"
      ), //<anything>/[Front|Back|Main]/Door
      displayName: "Main door sensor",
      img: "/images/installation/sensors/door/door_thumb.png",
      splinePNG: "/images/installation/spline/door.png",
      alt: "door icon",
      colour: theme.unexpected3,
      locationRequired: true,
      locationOptions: [
        { value: "front door", label: "Front door" },
        { value: "back door", label: "Back door" },
      ],
    },
    {
      defaultName: "Window Sensor",
      nameRegex: new RegExp("[\\w]+/Secondary/Door$", "i"), //<anything>/[Secondary]/Door
      displayName: "Second door sensor",
      img: "/images/installation/sensors/door/door_thumb.png",
      splinePNG: "/images/installation/spline/door.png",
      alt: "door icon",
      colour: theme.unexpected3,
      locationRequired: true,
      locationOptions: [
        { value: "back door", label: "Back door" },
        { value: "balcony door", label: "Balcony door" },
        { value: "garden door", label: "Garden door" },
        { value: "garage door", label: "Garage door" },
        { value: "side door", label: "Side door" },
      ],
    },
    {
      defaultName: "Vibration Sensor",
      nameRegex: new RegExp("[\\w]+/[\\w]+/Vibration$", "i"), //<anything>/<anything>/Vibration
      displayName: "Vibration sensor",
      img: "/images/installation/sensors/vibration/vibration_thumb.png",
      alt: "vibration sensor icon",
      colour: theme.white,
      locationRequired: false,
    },
    {
      defaultName: "Air Quality Sensor",
      nameRegex: new RegExp("[\\w]+/[\\w]+/VOC", "i"), //<anything>/<anything>/VOC  (which stands for volatile organic compounds)
      displayName: "Air quality sensor",
      img: "/images/installation/sensors/airquality/airquality_thumb.png",
      alt: "air quality sensor icon",
      colour: theme.white,
      locationRequired: false,
    },
    {
      defaultName: "Humidity Sensor",
      nameRegex: new RegExp("[\\w]+/[\\w]+/Humidity", "i"), //<anything>/<anything>/Humidity
      displayName: "Humidity sensor",
      img: "/images/installation/sensors/humidity/humidity_thumb.png",
      alt: "humidity sensor icon",
      colour: theme.white,
      locationRequired: false,
    },
  ];
  // Combine the devices data from the API with the additional data above
  const augmentedSensorData = devices?.map((sensor) => {
    const additionalSensorData = additionalData.find((data) =>
      data.nameRegex.test(sensor.name)
    );
    return { ...sensor, ...additionalSensorData };
  });

  return augmentedSensorData;
};
